import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import Seo from "@/components/feature/Seo";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/categories";

const statusOptions = ["全部状态", "持续跟进", "已回应", "已归档"] as const;

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const initialQuery = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [status, setStatus] = useState<(typeof statusOptions)[number]>("全部状态");
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  const currentCategory = categories.find((c) => c.id === activeCategory);

  const list = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    let result = events.filter((item) => {
      const matchCategory = activeCategory === "all" || item.categoryId === activeCategory;
      const matchStatus = status === "全部状态" || item.status === status;
      const matchQuery =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchStatus && matchQuery;
    });
    result = [...result].sort((a, b) => (sort === "hot" ? b.heat - a.heat : a.date < b.date ? 1 : -1));
    return result;
  }, [activeCategory, keyword, status, sort]);

  const handleCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setKeyword("");
    setStatus("全部状态");
    setSort("latest");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const pageTitle = currentCategory
    ? `${currentCategory.name}热点事件档案 · 瓜田观察站`
    : "事件档案库 · 网络热点时间线整理 · 瓜田观察站";
  const pageDesc = currentCategory
    ? `瓜田观察站${currentCategory.name}分类下的事件档案合集，${currentCategory.desc}按更新时间排序，附时间线与网友讨论。`
    : "浏览瓜田观察站全部网络热点事件档案，支持按直播圈、短视频、电商带货等分类筛选与关键词搜索，每条档案都附完整时间线。";
  const pageKeywords = currentCategory
    ? `${currentCategory.name},事件档案,时间线,热点整理,吃瓜观察`
    : "事件档案库,网络热点,分类筛选,时间线整理,吃瓜观察";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: currentCategory ? `${currentCategory.name}事件档案` : "事件档案库",
      description: pageDesc,
      url: "https://9jzs.cn/topics",
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", name: "瓜田观察站", url: "https://9jzs.cn" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://9jzs.cn/" },
        { "@type": "ListItem", position: 2, name: "事件档案库", item: "https://9jzs.cn/topics" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "事件档案列表",
      itemListElement: list.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://9jzs.cn/topic/${item.id}`,
        name: item.title,
      })),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        path="/topics"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 pb-10 pt-24 md:pb-14 md:pt-32">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <Breadcrumb items={[{ label: "事件档案库" }]} />
            <h1 className="mt-5 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-5xl">
              {currentCategory ? `${currentCategory.name}档案` : "事件档案库"}
            </h1>
            <p className="mt-4 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {currentCategory
                ? currentCategory.desc
                : "所有已整理的公开热点档案都在这里。你可以按分类筛选，也可以直接搜索关键词，每条档案都以时间线形式呈现。"}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-label text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-archive-2-line text-primary-500" aria-hidden="true"></i>
                当前 {list.length} 份档案
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-secondary-500" aria-hidden="true"></i>
                更新于 2026-09-10
              </span>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-[260px_1fr] lg:gap-10">
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <label className="relative block">
                  <span className="sr-only">搜索事件档案</span>
                  <i
                    className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                    aria-hidden="true"
                  ></i>
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索标题或标签"
                    className="h-11 w-full rounded-md border border-background-200 bg-background-100 pl-10 pr-3 font-body text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                  />
                </label>

                <h2 className="mt-6 font-label text-xs font-medium tracking-wide text-foreground-500">内容分类</h2>
                <div className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleCategory("all")}
                    className={`rounded-md px-3 py-2 text-left font-body text-sm transition-colors duration-200 ${
                      activeCategory === "all"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    全部分类
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategory(category.id)}
                      className={`flex items-center gap-2 rounded-md px-3 py-2 text-left font-body text-sm transition-colors duration-200 ${
                        activeCategory === category.id
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      <i className={category.icon} aria-hidden="true"></i>
                      {category.name}
                    </button>
                  ))}
                </div>

                <h2 className="mt-6 font-label text-xs font-medium tracking-wide text-foreground-500">事件状态</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {statusOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setStatus(option)}
                      className={`whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors duration-200 ${
                        status === option
                          ? "border-secondary-400 bg-secondary-100 text-secondary-900"
                          : "border-background-200 text-foreground-600 hover:border-secondary-300"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                <button type="button" onClick={resetFilters} className="btn-secondary mt-6 w-full">
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  重置筛选
                </button>
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-body text-sm text-foreground-600">
                  共找到 <strong className="font-bold text-foreground-950">{list.length}</strong> 份档案
                </p>
                <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    className={`rounded-full px-3.5 py-1.5 font-label text-xs transition-colors duration-200 ${
                      sort === "latest" ? "bg-foreground-950 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    最新更新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`rounded-full px-3.5 py-1.5 font-label text-xs transition-colors duration-200 ${
                      sort === "hot" ? "bg-foreground-950 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    热度优先
                  </button>
                </div>
              </div>

              {list.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                  <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true"></i>
                  <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的档案</p>
                  <p className="mt-2 font-body text-sm text-foreground-500">
                    换一个关键词，或者把筛选条件放宽一点试试。
                  </p>
                  <button type="button" onClick={resetFilters} className="btn-primary mt-6">
                    重置全部条件
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}